/**
 * Session management RPC methods.
 *
 * Wraps the gateway's sessions.list, sessions.patch, sessions.reset,
 * agents.list, and models.list RPC methods for use in the voice app.
 */

import type { WSClient } from '../ws/client';
import type {
  AgentsListResult,
  SessionsListResult,
  ModelsListResult,
  SessionPatchParams,
  SessionPatchResult,
  SessionCreateParams,
  SessionCreateResult,
  SessionDeleteResult,
  SessionResetResult,
  AgentPatchParams,
  AgentPatchResult,
} from '../types/sessions';

/**
 * List all agents configured on the gateway.
 */
export async function listAgents(client: WSClient): Promise<AgentsListResult> {
  return client.request<Record<string, never>, AgentsListResult>('agents.list', {});
}

/**
 * List all sessions from the gateway.
 *
 * @param client - WebSocket client
 * @param opts - Optional filters (agentId, activeMinutes, limit, etc.)
 */
export async function listSessions(
  client: WSClient,
  opts?: {
    agentId?: string;
    activeMinutes?: number;
    limit?: number;
    includeDerivedTitles?: boolean;
    includeLastMessage?: boolean;
    search?: string;
  },
): Promise<SessionsListResult> {
  return client.request<Record<string, unknown>, SessionsListResult>('sessions.list', {
    includeDerivedTitles: true,
    includeLastMessage: true,
    ...opts,
  });
}

/**
 * List available models from the gateway.
 */
export async function listModels(client: WSClient): Promise<ModelsListResult> {
  return client.request<Record<string, never>, ModelsListResult>('models.list', {});
}

/**
 * Patch session properties (model, thinking level, etc.).
 *
 * @param client - WebSocket client
 * @param params - Patch parameters including session key and fields to update
 */
export async function patchSession(
  client: WSClient,
  params: SessionPatchParams,
): Promise<SessionPatchResult> {
  return client.request<SessionPatchParams, SessionPatchResult>('sessions.patch', params);
}

/**
 * Create a fresh session for an agent without replacing an existing one.
 */
export async function createSession(
  client: WSClient,
  params: SessionCreateParams,
): Promise<SessionCreateResult> {
  return client.request<SessionCreateParams, SessionCreateResult>('sessions.create', params);
}

/**
 * Archive/remove a session from the overview.
 */
export async function deleteSession(
  client: WSClient,
  key: string,
): Promise<SessionDeleteResult> {
  return client.request<{ key: string }, SessionDeleteResult>('sessions.delete', { key });
}

/**
 * Reset a session (archives transcript, creates new session ID).
 *
 * @param client - WebSocket client
 * @param key - Session key to reset
 * @param reason - Reset reason ('reset' or 'new')
 */
export async function resetSession(
  client: WSClient,
  key: string,
  reason: 'reset' | 'new' = 'reset',
): Promise<SessionResetResult> {
  return client.request<{ key: string; reason: string }, SessionResetResult>(
    'sessions.reset',
    { key, reason },
  );
}

/**
 * Patch agent configuration (default model, etc.).
 *
 * Updates the agent's gateway config — unlike session patches,
 * this changes the agent's default model, not a session override.
 *
 * @param client - WebSocket client
 * @param params - Patch parameters including agent ID and fields to update
 */
export async function patchAgent(
  client: WSClient,
  params: AgentPatchParams,
): Promise<AgentPatchResult> {
  return client.request<AgentPatchParams, AgentPatchResult>('agents.patch', params);
}
